/**
 * loop-price-sync.js
 * ------------------------------------------------------------------
 * Keeps the visible PDP price AND the "% Off" discount badge in sync with the
 * Loop Subscriptions widget.
 *
 * Problem it solves:
 *   The Loop subscription widget is an app block. Selecting "Subscribe & Save"
 *   only chooses a selling plan â€” it is NOT a Shopify variant change â€” so the
 *   theme's <product-price> element (which only reacts to `variant:update`)
 *   never updates, and the PDP keeps showing the one-time price + one-time % off.
 *
 * What it does:
 *   - Reads accurate subscription price + discount % from the #loop-price-data
 *     JSON island (rendered by snippets/loop.plan.liquid from selling_plan_allocations
 *     and the Estimated Retail Value metafield).
 *   - When a subscription option is active in the Loop widget, it replaces the
 *     visible price and recalculates the "% Off" badge; when "One time" is chosen,
 *     it restores the originals.
 *   - Re-applies correctly after the theme re-renders the price on variant change.
 *
 * Loop widget DOM (confirmed): the purchase options are role="radio" divs
 *   `.loop-widget-purchase-option` inside `.loop-widget-container`. The selected
 *   one has aria-checked="true". The one-time option carries the attribute
 *   `data-loop-widget-onetime-purchase-option` (and class `...-onetime`).
 *   Subscription is "active" when the checked option is NOT the one-time option.
 *   The selected selling plan id is mirrored into the theme hidden select
 *   (.loop-plan-widget select[name='selling_plan']).
 * ------------------------------------------------------------------
 */
(function () {
  'use strict';

  var LOOP_CONTAINER = '.loop-widget-container';
  var OPTION = '.loop-widget-purchase-option';
  var ONETIME_ATTR = 'data-loop-widget-onetime-purchase-option';
  var ONETIME_CLASS = 'loop-widget-purchase-option-onetime';
  var PRICE_SEL = 'product-price .price-sale-wrapper .price';
  var PCT_SEL = 'product-price .percentage-discount, product-price .discount-percentage';
  var PCT_RE = /\d+(\.\d+)?\s*%/;

  var PRICE_MAP = {};
  var dataEl = document.getElementById('loop-price-data');
  if (dataEl) {
    try {
      PRICE_MAP = JSON.parse(dataEl.textContent || '{}');
    } catch (e) {
      PRICE_MAP = {};
    }
  }

  /** The product section that contains the Loop widget (falls back to document). */
  function scope() {
    var c = document.querySelector(LOOP_CONTAINER);
    return (c && c.closest('.shopify-section')) || document;
  }

  /** Currently selected variant id from the add-to-cart form. */
  function getVariantId() {
    var s = scope();
    var el =
      s.querySelector('[name="id"]') ||
      document.querySelector('form[action*="/cart/add"] [name="id"]') ||
      document.querySelector('[name="id"]');
    return el && el.value ? String(el.value) : null;
  }

  /** The purchase option currently checked in the Loop widget, or null. */
  function checkedOption() {
    var c = document.querySelector(LOOP_CONTAINER);
    if (!c) return null;
    var options = c.querySelectorAll(OPTION);
    for (var i = 0; i < options.length; i++) {
      if (options[i].getAttribute('aria-checked') === 'true') return options[i];
    }
    return null;
  }

  /** True when a subscription option (not "one time") is the checked option. */
  function subscriptionActive() {
    var opt = checkedOption();
    if (!opt) return false;
    var isOneTime =
      opt.hasAttribute(ONETIME_ATTR) || opt.classList.contains(ONETIME_CLASS);
    return !isOneTime;
  }

  /** Selected selling plan id (mirrored into the theme hidden select by Loop). */
  function getPlanId() {
    var el =
      document.querySelector(".loop-plan-widget select[name='selling_plan']") ||
      document.querySelector("select[name='selling_plan']");
    if (el && el.value) return String(el.value);
    var v = getVariantId();
    if (v && PRICE_MAP[v]) {
      var keys = Object.keys(PRICE_MAP[v]);
      if (keys.length === 1) return keys[0];
    }
    return null;
  }

  /** { price, pct } for the current variant + plan, or null. */
  function subscriptionInfo() {
    var v = getVariantId();
    var p = getPlanId();
    if (!v || !p) return null;
    var forVariant = PRICE_MAP[v];
    return forVariant && forVariant[p] ? forVariant[p] : null;
  }

  function priceSpans() {
    var s = scope();
    var found = s.querySelectorAll(PRICE_SEL);
    if (!found.length && s !== document) found = document.querySelectorAll(PRICE_SEL);
    return found;
  }

  function pctEls() {
    var s = scope();
    var found = s.querySelectorAll(PCT_SEL);
    if (!found.length && s !== document) found = document.querySelectorAll(PCT_SEL);
    return found;
  }

  function render() {
    var info = subscriptionActive() ? subscriptionInfo() : null;

    // 1) Price
    priceSpans().forEach(function (span) {
      if (span.dataset.loopOneTime == null) span.dataset.loopOneTime = span.innerHTML;
      if (info && info.price) {
        if (span.dataset.loopApplied !== '1' || span.textContent.trim() !== info.price) {
          span.textContent = info.price;
          span.dataset.loopApplied = '1';
        }
      } else if (span.dataset.loopApplied === '1') {
        span.innerHTML = span.dataset.loopOneTime || span.innerHTML;
        span.dataset.loopApplied = '';
      }
    });

    // 2) Discount "% Off" badge (keep surrounding wording, swap the number)
    pctEls().forEach(function (el) {
      if (el.dataset.loopPctOrig == null) el.dataset.loopPctOrig = el.textContent;
      if (info && info.pct && info.pct > 0 && PCT_RE.test(el.dataset.loopPctOrig || el.textContent)) {
        var next = (el.dataset.loopPctOrig || el.textContent).replace(PCT_RE, info.pct + '%');
        if (el.textContent !== next) el.textContent = next;
        el.dataset.loopPctApplied = '1';
      } else if (el.dataset.loopPctApplied === '1') {
        el.textContent = el.dataset.loopPctOrig || el.textContent;
        el.dataset.loopPctApplied = '';
      }
    });
  }

  var timer;
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(render, 60);
  }

  function wire(container) {
    var mo = new MutationObserver(schedule);
    mo.observe(container, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-checked', 'class', 'style'],
    });
    container.addEventListener('change', schedule, true);
    container.addEventListener('click', schedule, true);

    // The theme replaces the price container on variant change; re-cache the fresh
    // one-time price + %, then re-apply the subscription values if still selected.
    document.addEventListener('variant:update', function () {
      setTimeout(function () {
        priceSpans().forEach(function (span) {
          span.dataset.loopOneTime = span.innerHTML;
          span.dataset.loopApplied = '';
        });
        pctEls().forEach(function (el) {
          el.dataset.loopPctOrig = el.textContent;
          el.dataset.loopPctApplied = '';
        });
        render();
      }, 60);
    });

    render();
  }

  function start() {
    var c = document.querySelector(LOOP_CONTAINER);
    if (c) {
      wire(c);
      return;
    }
    var obs = new MutationObserver(function () {
      var el = document.querySelector(LOOP_CONTAINER);
      if (el) {
        obs.disconnect();
        wire(el);
      }
    });
    obs.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(function () {
      obs.disconnect();
    }, 15000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
